import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BookOpen } from 'lucide-react';
import { ReportHeader } from '../components/ReportHeader';
import { fmt, fmtDT, StatusPill, Spinner, ErrorBanner, PaginationBar, usePii, UNAVAILABLE } from '../components/ReportTableKit';
import LearnerHistoryDrawer from '../components/LearnerHistoryDrawer';
import CourseLearnersDrawer from '../components/CourseLearnersDrawer';

const linkStyle = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#4338ca', fontWeight: 600, textAlign: 'left', fontSize: 'inherit', fontFamily: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2 };

const API = process.env.REACT_APP_BACKEND_URL;

export default function InsightsCubeCourseActivity() {
  const [period, setPeriod] = useState({ startDate: '', endDate: '' });
  const [searchInput, setSearchInput] = useState('');
  const [applied, setApplied] = useState({ search: '', sort: 'enrolled_at:desc' });
  const [sort, setSort] = useState('enrolled_at:desc');
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, last_page: 1 });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const pii = usePii();
  const [drill, setDrill] = useState(null);
  const [courseDrill, setCourseDrill] = useState(null);
  const PER = 100;

  const handlePeriod = useCallback(({ startDate, endDate }) => setPeriod({ startDate: startDate || '', endDate: endDate || '' }), []);

  const fetchData = useCallback(async (p, f) => {
    setLoading(true); setErr(null);
    try {
      const params = { page: p, per_page: PER, sort: f.sort };
      if (f.search) params.search = f.search;
      if (period.startDate) params.date_from = period.startDate;
      if (period.endDate) params.date_to = period.endDate;
      const r = await axios.get(`${API}/api/lup/v2/report/course-activity`, { params, withCredentials: true });
      setRows(r.data?.data || []);
      setMeta({ total: r.data?.total || 0, last_page: r.data?.last_page || 1 });
      setPage(r.data?.current_page || p);
    } catch (e) {
      setErr(UNAVAILABLE('Course activity')); setRows([]); setMeta({ total: 0, last_page: 1 });
    } finally { setLoading(false); }
  }, [period.startDate, period.endDate]);

  useEffect(() => { fetchData(1, applied); }, [fetchData, applied]);

  const apply = () => setApplied({ search: searchInput, sort });
  const reset = () => { setSearchInput(''); setSort('enrolled_at:desc'); setApplied({ search: '', sort: 'enrolled_at:desc' }); };

  return (
    <div data-testid="insights-course-activity-page">
      <ReportHeader reportTitle="Course Activity (MAQ + PSH)" onChange={handlePeriod} />
      <div className="page-header">
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}><BookOpen size={22} color="#4338ca" /> Insights Cube — Course Activity</h1>
        <p>One row per learner per active course enrollment, with live progress — sourced from the LevelUp reporting API.</p>
      </div>
      <div className="filter-bar" style={{ alignItems: 'flex-end' }}>
        <div className="filter-group"><label className="filter-label">Search</label>
          <input type="text" className="filter-input" placeholder="User ID, email, course…" value={searchInput} onChange={e => setSearchInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && apply()} data-testid="course-activity-search" /></div>
        <div className="filter-group"><label className="filter-label">Sort</label>
          <select className="filter-select" value={sort} onChange={e => setSort(e.target.value)} data-testid="course-activity-sort">
            <option value="enrolled_at:desc">Enrolled (newest)</option><option value="enrolled_at:asc">Enrolled (oldest)</option>
            <option value="completed_at:desc">Completed (newest)</option><option value="completed_at:asc">Completed (oldest)</option></select></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" onClick={apply} data-testid="course-activity-apply-btn">Apply</button>
          <button className="btn btn-secondary" onClick={reset} data-testid="course-activity-reset-btn">Reset</button></div>
      </div>
      <div className="table-container">
        {loading ? <Spinner /> : err ? <ErrorBanner message={err} testId="course-activity-error" /> : rows.length === 0 ?
          <div className="empty-state"><h3>No enrollments found</h3><p>Try adjusting your filters.</p></div> : (<>
            <div className="table-header"><span className="table-title">Course Activity</span><span className="table-count" style={{ marginLeft: 8 }}>({fmt(meta.total)} rows)</span></div>
            <div className="table-scroll"><table data-testid="course-activity-table"><thead><tr>
              <th>Platform</th>{pii && <th>Learner</th>}{pii && <th>Email</th>}<th>LMS ID</th><th>Org</th><th>Country</th><th>Course</th><th>Status</th><th className="td-right">Progress</th><th>Enrolled</th><th>Completed</th>
            </tr></thead><tbody>{rows.map((row, i) => <tr key={i} data-testid={`course-activity-row-${i}`}>
              <td style={{ fontSize: 12 }}>{row.platform || '—'}</td>
              {pii && <td style={{ fontSize: 13 }}>{row.student_user_id != null ? <button style={linkStyle} onClick={() => setDrill({ lmsId: row.student_user_id, name: row.learner_name })} data-testid={`course-activity-learner-link-${i}`}>{row.learner_name || '—'}</button> : (row.learner_name || '—')}</td>}
              {pii && <td style={{ fontSize: 12, fontFamily: 'monospace', color: '#3182ce' }}>{row.email || '—'}</td>}
              <td style={{ fontSize: 12, fontFamily: 'monospace' }}>{row.student_user_id != null ? <button style={linkStyle} onClick={() => setDrill({ lmsId: row.student_user_id, name: row.learner_name })} data-testid={`course-activity-lmsid-link-${i}`}>{row.student_user_id}</button> : '—'}</td>
              <td style={{ fontSize: 12 }}>{row.organization || '—'}</td>
              <td style={{ fontSize: 12 }}>{row.country || '—'}</td>
              <td style={{ fontSize: 13 }}>{row.course_name ? <button style={linkStyle} onClick={() => setCourseDrill(row.course_name)} data-testid={`course-activity-course-link-${i}`}>{row.course_name}</button> : '—'}</td>
              <td><StatusPill status={row.enrollment_status} /></td>
              <td className="td-right" style={{ fontWeight: 600 }}>{row.progress_pct != null ? `${row.progress_pct}%` : '—'}</td>
              <td style={{ fontSize: 12 }}>{fmtDT(row.enrolled_at)}</td>
              <td style={{ fontSize: 12 }}>{fmtDT(row.completed_at)}</td>
            </tr>)}</tbody></table></div>
            <PaginationBar page={page} totalPages={meta.last_page} total={meta.total} perPage={PER} onPage={p => fetchData(p, applied)} testId="course-activity-pagination" />
          </>)}
      </div>
      {drill && <LearnerHistoryDrawer lmsId={drill.lmsId} learnerName={drill.name} pii={pii} onClose={() => setDrill(null)} />}
      {courseDrill && <CourseLearnersDrawer courseName={courseDrill} pii={pii} onClose={() => setCourseDrill(null)} onLearner={(l) => { setCourseDrill(null); setDrill(l); }} />}
    </div>
  );
}
